import React from "react";
import { X, Copy, Check } from "lucide-react";
import { FieldDefinition } from "./types";
import { isArrayField, formatDate } from "./utils";

interface DynamicFieldProps {
  fieldName: string;
  fieldDef: FieldDefinition;
  value: unknown;
  isEditing: boolean;
  copied: string | null;
  onInputChange: (field: string, value: unknown) => void;
  onArrayItemChange: (field: string, index: number, value: string) => void;
  onAddArrayItem: (field: string) => void;
  onRemoveArrayItem: (field: string, index: number) => void;
  onCopySection: (text: string, sectionName: string) => void;
}

// Markdownをセマンティックに表示するヘルパー関数
const renderMarkdown = (text: string): string => {
  if (!text) return "";
  
  let processedText = text;
  
  // 不要なmarkdownコードブロックと不要な行を除去
  processedText = processedText
    .replace(/^```markdown\n/g, '') // 先頭の```markdownを除去
    .replace(/\n```$/g, '') // 末尾の```を除去
    .split('\n') // 行ごとに分割
    .filter(line => {
      const trimmedLine = line.trim();
      // 「営業日報」、「Content」、「content」、空行を除去
      return trimmedLine !== '営業日報' && 
             trimmedLine !== 'Content' && 
             trimmedLine !== 'content' &&
             trimmedLine !== '';
    })
    .join('\n'); // 再結合
  
  // 改行を保持しつつ、連続する改行は段落として扱う
  processedText = processedText.replace(/\n\s*\n/g, '\n\n<div class="my-4"></div>\n\n');
  
  // インデント付きリスト（ネストされたリスト）をサポート
  const lines = processedText.split('\n');
  const result: string[] = [];
  let inList = false;
  let listStack: { indent: number; type: 'bullet' | 'number' }[] = [];
  
  // 階層に応じたリストスタイルを決定する関数
  const getListClass = (depth: number, type: 'bullet' | 'number'): string => {
    if (type === 'bullet') {
      // 箇条書きリスト：disc -> circle -> square
      const styles = ['list-disc', 'list-circle', 'list-square'];
      return styles[depth % styles.length];
    } else {
      // 数字リスト：decimal -> lower-alpha -> lower-roman
      const styles = ['list-decimal', 'list-[lower-alpha]', 'list-[lower-roman]'];
      return styles[depth % styles.length];
    }
  };
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const bulletMatch = line.match(/^( *)[-*+] (.+)$/);
    const numberMatch = line.match(/^( *)\d+\. (.+)$/);
    
    if (bulletMatch || numberMatch) {
      const indent = (bulletMatch || numberMatch)?.[1].length || 0;
      const content = (bulletMatch || numberMatch)?.[2] || '';
      const currentType = bulletMatch ? 'bullet' : 'number';
      
      if (!inList) {
        const listClass = getListClass(0, currentType);
        result.push(`<ul class="${listClass} space-y-2 ml-6 my-4">`);
        inList = true;
        listStack = [{ indent, type: currentType }];
      } else {
        const lastLevel = listStack[listStack.length - 1];
        if (indent > lastLevel.indent) {
          // より深い階層に入る
          const depth = listStack.length;
          const listClass = getListClass(depth, currentType);
          const listTag = currentType === 'number' ? 'ol' : 'ul';
          result.push(`<${listTag} class="${listClass} space-y-1 ml-6 mt-2">`);
          listStack.push({ indent, type: currentType });
        } else if (indent < lastLevel.indent) {
          // 浅い階層に戻る
          while (listStack.length > 0 && listStack[listStack.length - 1].indent > indent) {
            const poppedLevel = listStack.pop();
            const listTag = poppedLevel?.type === 'number' ? 'ol' : 'ul';
            result.push(`</${listTag}>`);
          }
          // 同じレベルまたは新しいレベルの処理
          if (listStack.length === 0 || listStack[listStack.length - 1].indent !== indent) {
            const depth = listStack.length;
            const listClass = getListClass(depth, currentType);
            const listTag = currentType === 'number' ? 'ol' : 'ul';
            result.push(`<${listTag} class="${listClass} space-y-2 ml-6 my-4">`);
            listStack.push({ indent, type: currentType });
          }
        }
      }
      
      result.push(`<li class="text-gray-900 dark:text-gray-100 leading-relaxed mt-6 mb-6">${content}</li>`);
    } else {
      if (inList) {
        while (listStack.length > 0) {
          const poppedLevel = listStack.pop();
          const listTag = poppedLevel?.type === 'number' ? 'ol' : 'ul';
          result.push(`</${listTag}>`);
        }
        inList = false;
      }
      result.push(line);
    }
  }
  
  // 残りのリストタグを閉じる
  while (listStack.length > 0) {
    const poppedLevel = listStack.pop();
    const listTag = poppedLevel?.type === 'number' ? 'ol' : 'ul';
    result.push(`</${listTag}>`);
  }
  
  processedText = result.join('\n');

  return processedText
    // 見出し処理（スペーシング最適化版）
    .replace(/^### (.*$)/gm, '<h3 class="text-lg font-semibold mt-6 mb-6 text-gray-900 dark:text-gray-100 pb-2">$1</h3>')
    .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold mt-6 mb-6 text-gray-900 dark:text-gray-100 pb-2">$1</h2>')
    .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold mt-6 mb-6 text-gray-900 dark:text-gray-100 pb-2">$1</h1>')
    // 強調とコード
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-gray-100">$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em class="italic text-gray-700 dark:text-gray-300">$1</em>')
    .replace(/`([^`]+)`/g, '<code class="px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-sm font-mono border">$1</code>')
    // リンクと引用
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline font-medium" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/^> (.*)$/gm, '<blockquote class="pl-4 border-l-4 border-blue-400 dark:border-blue-500 my-4 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 py-2 rounded-r italic">$1</blockquote>')
    // 水平線
    .replace(/^---$/gm, '<hr class="my-6 border-t-2 border-gray-200 dark:border-gray-700">');
};

export const DynamicField: React.FC<DynamicFieldProps> = ({
  fieldName,
  fieldDef,
  value,
  isEditing,
  copied,
  onInputChange,
  onArrayItemChange,
  onAddArrayItem,
  onRemoveArrayItem,
  onCopySection
}) => {
  const isArray = isArrayField(fieldDef);
  const stringValue = typeof value === 'string' ? value : '';
  const arrayValue = Array.isArray(value) ? value : [];

  // 特別なフィールド表示
  if (fieldName === 'participants' && isArray) {
    // 編集中でない場合、参加者が空なら非表示
    if (!isEditing && (!arrayValue || arrayValue.length === 0)) {
      return null;
    }
    
    return (
      <div className="mb-8 transition-opacity duration-500">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200/50 dark:border-gray-700/50">
          {fieldDef.description}
        </h2>
        <div className="mt-3">
          {isEditing ? (
            <div className="space-y-3">
              {arrayValue.map((participant: string, index: number) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={participant}
                    onChange={(e) => onArrayItemChange(fieldName, index, e.target.value)}
                    className="text-gray-900 dark:text-gray-100 bg-white/70 dark:bg-gray-700/70 px-3 py-2 rounded-md text-base flex-grow focus:outline-none focus:ring-2 focus:ring-blue-500/70 border border-gray-300/50 dark:border-gray-600/50 backdrop-blur-sm"
                    placeholder={fieldDef.description}
                  />
                  <button
                    onClick={() => onRemoveArrayItem(fieldName, index)}
                    className="text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 p-2"
                    aria-label="削除"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => onAddArrayItem(fieldName)}
                className="mt-3 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
                aria-label={`${fieldDef.description}を追加`}  
              >
                + {fieldDef.description}を追加
              </button>
            </div>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {arrayValue.map((participant: string, index: number) => (
                <li 
                  key={index} 
                  className="text-gray-900 dark:text-gray-100 bg-white/40 dark:bg-gray-800/40 backdrop-blur-sm px-4 py-2 rounded-md text-base border border-gray-200/30 dark:border-gray-700/30"
                >
                  {participant}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  // 基本情報系フィールド（client, datetime）
  if (['client', 'datetime'].includes(fieldName)) {
    return (
      <div className="p-4 bg-white/40 dark:bg-gray-800/40 backdrop-blur-sm rounded-md border border-gray-200/30 dark:border-gray-700/30">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{fieldDef.description}</p>
        {isEditing ? (
          fieldName === 'datetime' ? (
            <input
              type="datetime-local"
              value={stringValue ? new Date(stringValue).toLocaleString('sv-SE', { timeZone: 'Asia/Tokyo' }).slice(0, 16) : ""}
              onChange={(e) => onInputChange(fieldName, e.target.value)}
              className="w-full text-base bg-white/70 dark:bg-gray-700/70 border border-gray-300/70 dark:border-gray-600/70 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/70"
            />
          ) : (
            <input
              type="text"
              value={stringValue}
              onChange={(e) => onInputChange(fieldName, e.target.value)}
              className="w-full text-base bg-white/70 dark:bg-gray-700/70 border border-gray-300/70 dark:border-gray-600/70 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/70"
              placeholder={fieldDef.description}
            />
          )
        ) : (
          <p className="text-base text-gray-900 dark:text-gray-100 font-medium">
            {fieldName === 'datetime' ? formatDate(stringValue) : (stringValue || "未設定")}
          </p>
        )}
      </div>
    );
  }

  // 配列型フィールド（participants以外）
  if (isArray) {
    return (
      <div className="mb-8 transition-opacity duration-500">
        <div className="flex items-center justify-between gap-2 mb-4 pb-2 border-b border-gray-200/50 dark:border-gray-700/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {fieldDef.description}
          </h2>
          {!isEditing && arrayValue.length > 0 && (
            <button
              type="button"
              onClick={() => onCopySection(arrayValue.join('\n'), fieldName)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              title={copied === fieldName ? "コピーしました！": "クリップボードにコピー"}
            >
              {copied === fieldName ? (
                <span className="text-sm text-green-500 flex items-center">
                  <Check className="h-4 w-4 mr-1" />
                  コピーしました
                </span>
              ) : (
                <span className="text-sm flex items-center">
                  <Copy className="h-4 w-4 mr-1" />
                  コピー
                </span>
              )}
            </button>
          )}
        </div>
        <div className="mt-4">
          {isEditing ? (
            <div className="space-y-3">
              {arrayValue.map((item: string, index: number) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => onArrayItemChange(fieldName, index, e.target.value)}
                    className="text-gray-900 dark:text-gray-100 bg-white/70 dark:bg-gray-700/70 px-3 py-2 rounded-md text-base flex-grow focus:outline-none focus:ring-2 focus:ring-blue-500/70 border border-gray-300/50 dark:border-gray-600/50 backdrop-blur-sm"
                    placeholder={fieldDef.description}
                  />
                  <button
                    onClick={() => onRemoveArrayItem(fieldName, index)}
                    className="text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 p-2"
                    aria-label="削除"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => onAddArrayItem(fieldName)}
                className="mt-3 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
                aria-label={`${fieldDef.description}を追加`}  
              >
                + {fieldDef.description}を追加
              </button>
            </div>
          ) : (
            <ul className="space-y-2">
              {arrayValue.map((item: string, index: number) => (
                <li key={index} className="text-gray-900 dark:text-gray-100 bg-white/40 dark:bg-gray-800/40 backdrop-blur-sm px-4 py-2 rounded-md text-base border border-gray-200/30 dark:border-gray-700/30">
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  // 通常のテキストフィールド
  return (
    <div className="mb-8 transition-opacity duration-500">
      {/* Contentフィールドの場合はヘッダーを表示しない */}
      {fieldName !== 'Content' && fieldName !== 'content' && (
        <div className="flex items-center justify-between gap-2 mb-4 pb-2 border-b border-gray-200/50 dark:border-gray-700/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {fieldDef.description}
          </h2>
          {!isEditing && stringValue && (
            <button
              type="button"
              onClick={() => onCopySection(stringValue, fieldName)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              title={copied === fieldName ? "コピーしました！": "クリップボードにコピー"}
              aria-label={copied === fieldName ? "コピーしました！": "クリップボードにコピー"}
            >
              {copied === fieldName ? (
                <span className="text-sm text-green-500 flex items-center">
                  <Check className="h-4 w-4 mr-1" />
                  コピーしました
                </span>
              ) : (
                <span className="text-sm flex items-center">
                  <Copy className="h-4 w-4 mr-1" />
                  コピー
                </span>
              )}
            </button>
          )}
        </div>
      )}
      <div className="mt-4">
        {isEditing ? (
          <textarea
            value={stringValue}
            onChange={(e) => onInputChange(fieldName, e.target.value)}
            className="w-full text-gray-900 dark:text-gray-100 bg-white/70 dark:bg-gray-700/70 p-4 rounded-md min-h-32 border border-gray-300/50 dark:border-gray-600/50 focus:outline-none focus:ring-2 focus:ring-blue-500/70 backdrop-blur-sm"
            placeholder={`${fieldDef.description}を入力`}
          />
        ) : stringValue ? (
          <div className="prose text-gray-900 dark:text-gray-100 p-4 rounded-md">
            <div dangerouslySetInnerHTML={{ 
              __html: renderMarkdown(stringValue) 
            }} />
          </div>
        ) : null}
      </div>
    </div>
  );
}; 